import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import EditMonthlyReport from "./EditMonthlyReport";
import ViewMonthlyReport from "./ViewMonthlyReport";
import { AuthContext } from "../../contexts/AuthContext";
import { getApiBaseURL } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";

const MonthlyReportList = () => {
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [editingReport, setEditingReport] = useState(null);
  const [viewingReport, setViewingReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    dateFrom: "",
    dateTo: "",
    service: "",
    counselor: "",
  });

  // Filter options
  const [statuses, setStatuses] = useState([]);
  const [services, setServices] = useState([]);
  const [counselors, setCounselors] = useState([]);

  const { tenant_id, user } = useContext(AuthContext);
  const apiBase = getApiBaseURL();

  useEffect(() => {
    if (tenant_id && user?.user_id) {
      fetchReports();
      fetchFilterOptions();
    }
  }, [tenant_id, user]);

  const fetchFilterOptions = async () => {
    try {
      // Fetch statuses with status_type_id = 1
      const statusRes = await secureAxios.get(`${apiBase}/api/status`);
      const monthlyReportStatuses = statusRes.data.filter(
        (status) => status.status_type_id === 1
      );
      setStatuses(monthlyReportStatuses);

      // Extract unique services and counselors from reports data
      // This will be populated after reports are fetched
    } catch (err) {
      // Silent fail for filter options
    }
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const res = await secureAxios.get(`${apiBase}/api/reports/list`, {
        params: {
          tenant_id,
          user_id: user?.user_id,
          is_admin: user?.is_admin ? "true" : "false",
        },
      });
      setReports(res.data);
      setFilteredReports(res.data);

      // Extract unique services and counselors for filter dropdowns
      const uniqueServices = [
        ...new Set(res.data.map((r) => r.service_name).filter(Boolean)),
      ];
      const uniqueCounselors = [
        ...new Set(res.data.map((r) => r.counselor_name).filter(Boolean)),
      ];

      setServices(uniqueServices.sort());
      setCounselors(uniqueCounselors.sort());
    } catch (err) {
      alert("Unable to load reports. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Apply all filters
  useEffect(() => {
    let filtered = [...reports];

    // Search filter (consumer name or report number)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.consumer_name?.toLowerCase().includes(searchLower) ||
          r.report_number?.toLowerCase().includes(searchLower) ||
          r.authorization_number?.toLowerCase().includes(searchLower)
      );
    }

    // Status filter
    if (filters.status) {
      filtered = filtered.filter((r) => r.status_id === filters.status);
    }

    // Date range filter
    if (filters.dateFrom) {
      filtered = filtered.filter(
        (r) => new Date(r.date_of_report) >= new Date(filters.dateFrom)
      );
    }
    if (filters.dateTo) {
      filtered = filtered.filter(
        (r) => new Date(r.date_of_report) <= new Date(filters.dateTo)
      );
    }

    // Service filter
    if (filters.service) {
      filtered = filtered.filter((r) => r.service_name === filters.service);
    }

    // Counselor filter
    if (filters.counselor) {
      filtered = filtered.filter((r) => r.counselor_name === filters.counselor);
    }

    setFilteredReports(filtered);
  }, [filters, reports]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      status: "",
      dateFrom: "",
      dateTo: "",
      service: "",
      counselor: "",
    });
  };

  const handleEdit = (report) => {
    setEditingReport(report);
  };

  const handleView = async (report_id) => {
    try {
      const res = await secureAxios.get(`${apiBase}/api/reports/${report_id}`, {
        params: { tenant_id },
      });

      setViewingReport({
        ...res.data,
        tenant_id,
        report_id,
      });
    } catch (err) {
      alert("Failed to load report details.");
    }
  };

  const handleCloseEditOrView = () => {
    setEditingReport(null);
    setViewingReport(null);
    fetchReports();
  };

  // Function to get status badge color
  const getStatusBadgeClass = (status) => {
    if (!status) return "badge bg-secondary";

    const statusLower = status.toLowerCase();
    if (statusLower === "completed") return "badge bg-success";
    if (statusLower === "in progress") return "badge bg-primary";
    if (statusLower === "pending") return "badge bg-warning text-dark";
    if (statusLower === "cancelled") return "badge bg-danger";
    return "badge bg-secondary";
  };

  if (!user) return <p>Loading user...</p>;
  if (loading) return <p>Loading reports...</p>;

  return (
    <div>
      {!editingReport && !viewingReport && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2>All Monthly Reports</h2>
            <div>
              <span className="badge bg-info text-dark me-2">
                <i className="bi bi-eye me-1"></i>
                Access Logged
              </span>
              <button
                className="btn btn-outline-secondary"
                onClick={() => setShowFilters(!showFilters)}
              >
                <i className={`bi bi-funnel${showFilters ? "-fill" : ""}`}></i>{" "}
                Filters
                {Object.values(filters).filter(Boolean).length > 0 && (
                  <span className="badge bg-primary ms-2">
                    {Object.values(filters).filter(Boolean).length}
                  </span>
                )}
              </button>
            </div>
          </div>

          <div className="alert alert-info mb-3">
            <i className="bi bi-shield-lock me-2"></i>
            <strong>HIPAA Notice:</strong> This list contains Protected Health
            Information. Access is restricted to authorized personnel only. All
            viewing activities are logged for compliance.
          </div>

          {/* Filter Section */}
          {showFilters && (
            <div className="card mb-3">
              <div className="card-body">
                <div className="row g-3">
                  {/* Search */}
                  <div className="col-md-4">
                    <label className="form-label">Search</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Consumer, Report #, or Auth #..."
                      value={filters.search}
                      onChange={(e) =>
                        handleFilterChange("search", e.target.value)
                      }
                    />
                  </div>

                  {/* Status */}
                  <div className="col-md-4">
                    <label className="form-label">Status</label>
                    <select
                      className="form-select"
                      value={filters.status}
                      onChange={(e) =>
                        handleFilterChange("status", e.target.value)
                      }
                    >
                      <option value="">All Statuses</option>
                      {statuses.map((status) => (
                        <option key={status.status_id} value={status.status_id}>
                          {status.status}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Service */}
                  <div className="col-md-4">
                    <label className="form-label">Service</label>
                    <select
                      className="form-select"
                      value={filters.service}
                      onChange={(e) =>
                        handleFilterChange("service", e.target.value)
                      }
                    >
                      <option value="">All Services</option>
                      {services.map((service) => (
                        <option key={service} value={service}>
                          {service}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Date From */}
                  <div className="col-md-3">
                    <label className="form-label">Date From</label>
                    <input
                      type="date"
                      className="form-control"
                      value={filters.dateFrom}
                      onChange={(e) =>
                        handleFilterChange("dateFrom", e.target.value)
                      }
                    />
                  </div>

                  {/* Date To */}
                  <div className="col-md-3">
                    <label className="form-label">Date To</label>
                    <input
                      type="date"
                      className="form-control"
                      value={filters.dateTo}
                      onChange={(e) =>
                        handleFilterChange("dateTo", e.target.value)
                      }
                    />
                  </div>

                  {/* Counselor */}
                  <div className="col-md-4">
                    <label className="form-label">Counselor</label>
                    <select
                      className="form-select"
                      value={filters.counselor}
                      onChange={(e) =>
                        handleFilterChange("counselor", e.target.value)
                      }
                    >
                      <option value="">All Counselors</option>
                      {counselors.map((counselor) => (
                        <option key={counselor} value={counselor}>
                          {counselor}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Action Buttons */}
                  <div className="col-md-2 d-flex align-items-end">
                    <button
                      className="btn btn-secondary w-100"
                      onClick={clearFilters}
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>

                {/* Results Summary */}
                <div className="mt-3 text-muted">
                  Showing {filteredReports.length} of {reports.length} reports
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {editingReport ? (
        <EditMonthlyReport
          report={editingReport}
          onCancel={handleCloseEditOrView}
          onUpdated={handleCloseEditOrView}
        />
      ) : viewingReport ? (
        <ViewMonthlyReport
          report={viewingReport}
          onClose={handleCloseEditOrView}
        />
      ) : (
        <>
          {/* Desktop & Tablet View */}
          <div className="d-none d-md-block">
            <div className="table-responsive">
              <table className="table table-bordered table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Report #</th>
                    <th>Consumer</th>
                    <th>Date</th>
                    <th className="d-none d-lg-table-cell">Counselor</th>
                    <th className="d-none d-lg-table-cell">Authorization</th>
                    <th className="d-none d-md-table-cell">Service</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReports.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center">
                        No reports found matching your filters.
                      </td>
                    </tr>
                  ) : (
                    filteredReports.map((r) => (
                      <tr key={r.report_id}>
                        <td>{r.report_number || "—"}</td>
                        <td>{r.consumer_name || "—"}</td>
                        <td>{r.date_of_report?.split("T")[0]}</td>
                        <td className="d-none d-lg-table-cell">
                          {r.counselor_name || "—"}
                        </td>
                        <td className="d-none d-lg-table-cell">
                          {r.authorization_number || "—"}
                        </td>
                        <td className="d-none d-md-table-cell">
                          {r.service_name || "—"}
                        </td>
                        <td>
                          <span className={getStatusBadgeClass(r.status_name)}>
                            {r.status_name || "Pending"}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex gap-2 flex-nowrap">
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => handleView(r.report_id)}
                              title="View report (access will be logged)"
                            >
                              <i className="bi bi-eye"></i> View
                            </button>
                            {(user.is_admin ||
                              r.created_by === user.user_id ||
                              r.user_id === user.user_id) && (
                              <button
                                className="btn btn-sm btn-primary"
                                onClick={() => handleEdit(r)}
                                title="Edit report (changes will be logged)"
                              >
                                <i className="bi bi-pencil"></i> Edit
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile View */}
          <div className="d-block d-md-none">
            <ul className="list-group">
              {filteredReports.length === 0 ? (
                <li className="list-group-item text-center">
                  No reports found matching your filters.
                </li>
              ) : (
                filteredReports.map((r) => (
                  <li key={r.report_id} className="list-group-item">
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1">
                        <div>
                          <strong>Report #:</strong> {r.report_number || "—"}
                        </div>
                        <div>
                          <strong>Consumer:</strong> {r.consumer_name || "—"}
                        </div>
                        <div>
                          <strong>Date:</strong>{" "}
                          {r.date_of_report?.split("T")[0]}
                        </div>
                        <div>
                          <strong>Counselor:</strong> {r.counselor_name || "—"}
                        </div>
                        <div>
                          <strong>Authorization:</strong>{" "}
                          {r.authorization_number || "—"}
                        </div>
                        <div>
                          <strong>Service:</strong> {r.service_name || "—"}
                        </div>
                      </div>
                      <span className={getStatusBadgeClass(r.status_name)}>
                        {r.status_name || "Pending"}
                      </span>
                    </div>
                    <div className="mt-2 d-flex gap-2">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleView(r.report_id)}
                      >
                        <i className="bi bi-eye"></i> View
                      </button>
                      {(user.is_admin ||
                        r.created_by === user.user_id ||
                        r.user_id === user.user_id) && (
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleEdit(r)}
                        >
                          <i className="bi bi-pencil"></i> Edit
                        </button>
                      )}
                    </div>
                  </li>
                ))
              )}
            </ul>
          </div>

          <div className="text-center text-muted mt-4">
            <small>
              <i className="bi bi-info-circle me-1"></i>
              All report access is monitored and logged for HIPAA compliance.
            </small>
          </div>
        </>
      )}
    </div>
  );
};

export default MonthlyReportList;
